/**
 * ThemeOverview Component
 * Visar översikt över alla livsteman med progress och scores
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import lifeThemeAPI from '../../services/lifeThemeAPI';
import { trackEvent } from '../../utils/analytics';

const ThemeOverview = () => {
  const navigate = useNavigate();
  
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('id'); // 'id', 'median_score', 'progress'

  useEffect(() => {
    loadOverview();
    trackEvent('Life Theme Overview View Authenticated', {
      location: 'theme-overview-page',
      flow_type: 'authenticated_dashboard'
    });
  }, []);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const data = await lifeThemeAPI.getAllScores();
      setOverview(data);
    } catch (err) {
      console.error('Error loading overview:', err);
      setError('Kunde inte ladda översikt. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  };

  const getSortedThemes = () => {
    if (!overview) return [];
    
    const themes = [...overview.themes];
    
    switch (sortBy) {
      case 'median_score':
        return themes.sort((a, b) => {
          const scoreA = a.median_score || 0;
          const scoreB = b.median_score || 0;
          return scoreB - scoreA;
        });
      case 'progress':
        return themes.sort((a, b) => {
          const progressA = a.total_questions > 0 ? a.answered_questions / a.total_questions : 0;
          const progressB = b.total_questions > 0 ? b.answered_questions / b.total_questions : 0;
          return progressB - progressA;
        });
      default:
        return themes.sort((a, b) => a.theme_id - b.theme_id);
    }
  };

  const getScoreColor = (score) => {
    if (!score) return 'text-gray-400';
    if (score >= 5) return 'text-red-600';
    if (score >= 4) return 'text-orange-600';
    if (score >= 3) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getScoreBgColor = (score) => {
    if (!score) return 'bg-gray-100';
    if (score >= 5) return 'bg-red-100';
    if (score >= 4) return 'bg-orange-100';
    if (score >= 3) return 'bg-yellow-100';
    return 'bg-green-100';
  };

  const getElevationLevel = (elevatedScore) => {
    if (!elevatedScore) return { 
      level: 'Låg', 
      description: 'Minimal påverkan på ditt liv',
      color: 'text-green-600', 
      bgColor: 'bg-green-50', 
      borderColor: 'border-green-200' 
    };
    if (elevatedScore >= 50) return { 
      level: 'Mycket Hög', 
      description: 'Starkt aktivt livstema med stor påverkan',
      color: 'text-red-600', 
      bgColor: 'bg-red-50', 
      borderColor: 'border-red-300' 
    };
    if (elevatedScore >= 30) return { 
      level: 'Hög', 
      description: 'Aktivt livstema med betydande påverkan',
      color: 'text-orange-600', 
      bgColor: 'bg-orange-50', 
      borderColor: 'border-orange-300' 
    };
    if (elevatedScore >= 15) return { 
      level: 'Måttlig', 
      description: 'Måttligt aktivt livstema',
      color: 'text-yellow-600', 
      bgColor: 'bg-yellow-50', 
      borderColor: 'border-yellow-300' 
    };
    return { 
      level: 'Låg', 
      description: 'Låg påverkan på ditt liv',
      color: 'text-green-600', 
      bgColor: 'bg-green-50', 
      borderColor: 'border-green-200' 
    };
  };

  const getProgressPercentage = (theme) => {
    if (theme.total_questions === 0) return 0;
    return (theme.answered_questions / theme.total_questions) * 100;
  };

  const handleThemeClick = (theme) => {
    // Track theme click (authenticated)
    trackEvent('Life Theme Click Authenticated', {
      theme_id: theme.theme_id,
      theme_name: theme.theme_name,
      progress_percent: getProgressPercentage(theme),
      location: 'theme-overview',
      flow_type: 'authenticated_dashboard'
    });
    
    if (theme.answered_questions < theme.total_questions) {
      // Gå till progress/nästa fråga
      navigate(`/client/life-themes/${theme.theme_id}/progress`);
    } else {
      // Visa sammanfattning
      navigate(`/client/life-themes/${theme.theme_id}/summary`);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">{error || 'Översikt kunde inte laddas'}</p>
          <button
            onClick={loadOverview}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  const sortedThemes = getSortedThemes();

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Dina livsteman
        </h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
          <div className="text-sm font-medium text-gray-600 mb-2">
            Totalt antal teman
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {overview.total_themes}
          </div>
        </div>

        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
          <div className="text-sm font-medium text-green-700 mb-2">
            Slutförda teman
          </div>
          <div className="text-3xl font-bold text-green-900">
            {overview.completed_themes}
          </div>
        </div>

        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
          <div className="text-sm font-medium text-blue-700 mb-2">
            Besvarade frågor
          </div>
          <div className="text-3xl font-bold text-blue-900">
            {overview.total_answered} / {overview.total_questions}
          </div>
        </div>

        <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
          <div className="text-sm font-medium text-purple-700 mb-2">
            Total progress
          </div>
          <div className="text-3xl font-bold text-purple-900">
            {overview.overall_progress_percentage.toFixed(0)}%
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        {/* Button Group - Tailwind Style */}
        <div className="inline-flex rounded-lg shadow-sm" role="group">
          <button
            onClick={() => setSortBy('id')}
            className={`px-4 py-2 text-sm font-medium rounded-l-lg border transition-colors ${
              sortBy === 'id'
                ? 'bg-brand-primary text-white border-brand-primary z-10'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Ordning
          </button>
          <button
            onClick={() => setSortBy('median_score')}
            className={`px-4 py-2 text-sm font-medium border-t border-b transition-colors ${
              sortBy === 'median_score'
                ? 'bg-brand-primary text-white border-brand-primary z-10'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Styrka
          </button>
          <button
            onClick={() => setSortBy('progress')}
            className={`px-4 py-2 text-sm font-medium rounded-r-lg border transition-colors ${
              sortBy === 'progress'
                ? 'bg-brand-primary text-white border-brand-primary z-10'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Färdigställda
          </button>
        </div>

        <button
          onClick={() => navigate('/client/life-themes')}
          className="px-4 py-1.5 bg-brand-primary text-white rounded-md hover:bg-brand-primary-dark transition-colors text-sm font-medium"
        >
          Fortsätt besvara frågor
        </button>
      </div>

      {/* Themes Grid */}
      <div className="grid gap-4">
        {sortedThemes.map((theme) => {
          const progressPercentage = getProgressPercentage(theme);
          const isCompleted = theme.answered_questions === theme.total_questions;
          const elevation = getElevationLevel(theme.elevated_score);
          const isLowElevation = theme.elevated_score !== null && theme.elevated_score < 15;

          return (
            <button
              key={theme.theme_id}
              onClick={() => handleThemeClick(theme)}
              className="group text-left bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-200 border-2 border-gray-200"
            >
              <div className="flex items-start gap-6">
                {/* Theme Number - Green only if completed AND low elevation, otherwise elevation color or gray if not answered */}
                <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center font-bold text-xl transition-all border-2 ${
                  theme.elevated_score === null || theme.median_score === null
                    ? 'border-gray-200 bg-gray-100 text-gray-600'
                    : isCompleted && isLowElevation
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : `${elevation.borderColor} ${elevation.bgColor} ${elevation.color}`
                }`}>
                  {theme.theme_id}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Header with Name and Completion Badge */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-brand-primary transition-colors">
                      {theme.theme_name}
                    </h3>
                    {isCompleted && (
                      <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-600 text-white">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Slutfört
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {theme.theme_description}
                  </p>

                  {/* Elevation Description */}
                  {theme.elevated_score !== null && (
                    <div className="mb-4">
                      <p className={`text-sm font-medium ${elevation.color}`}>
                        {elevation.description}
                      </p>
                    </div>
                  )}

                  {/* Progress Bars */}
                  <div className="space-y-3 mb-3">
                    {/* Median Score Bar */}
                    {theme.median_score !== null && (
                      <div>
                        <div className="flex justify-between items-center text-xs mb-1">
                          <span className="text-gray-600 font-medium">Median svar</span>
                          <span className={`font-bold ${getScoreColor(theme.median_score)}`}>
                            {theme.median_score.toFixed(1)} / 6
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${
                              theme.median_score >= 5 ? 'bg-red-500' :
                              theme.median_score >= 4 ? 'bg-orange-500' :
                              theme.median_score >= 3 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${(theme.median_score / 6) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Elevated Score Bar */}
                    {theme.elevated_score !== null && (
                      <div>
                        <div className="flex justify-between items-center text-xs mb-1">
                          <span className="text-gray-600 font-medium">Höga svar (5-6)</span>
                          <span className={`font-bold ${elevation.color}`}>
                            {theme.elevated_score.toFixed(0)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${
                              theme.elevated_score >= 50 ? 'bg-red-500' :
                              theme.elevated_score >= 30 ? 'bg-orange-500' :
                              theme.elevated_score >= 15 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${theme.elevated_score}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bottom Row - Progress */}
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{theme.answered_questions}/{theme.total_questions} frågor besvarade</span>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0 self-center">
                  <svg
                    className="h-6 w-6 text-gray-300 group-hover:text-brand-primary transform group-hover:translate-x-1 transition-all"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ThemeOverview;
